import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button, Form, Offcanvas, Spinner } from 'react-bootstrap';
import { BsQuestionCircle } from 'react-icons/bs';
import { Descendant } from 'slate';

import { BayEmojiPicker } from '../../BayEmojiPicker';
import { dataFields } from '../../BayPolotnoEditor/dataFields';
import BayTooltip from '../../BayTooltip';

import DynamicInlineEditor, { DynamicInlineEditorRef } from './DynamicInlineEditor';
import VariableDropdown from './VariableDropdown';
import './CaptionEditor.scss';

type SlateNode = { type?: string; text?: string; value?: string; children?: SlateNode[] };

interface EditorValues {
	caption: Descendant[];
	linkTitle: Descendant[];
	linkDescription: Descendant[];
	url: string;
}

interface TemplateState {
	etc?: {
		caption?: string | SlateNode[];
		linkTitle?: string | SlateNode[];
		linkDescription?: string | SlateNode[];
		linkUrl?: string;
		url?: string;
	};
}

interface LinkPostFormProps {
	t: (key: string) => string;
	fieldOptions: (key: string, fields: any) => any[];
	linkTitleRef: React.RefObject<DynamicInlineEditorRef>;
	linkDescRef: React.RefObject<DynamicInlineEditorRef>;
	editorValues: EditorValues;
	setEditorValues: React.Dispatch<React.SetStateAction<EditorValues>>;
	handleEmojiSelect: (emoji: string, editorRef: React.RefObject<DynamicInlineEditorRef>) => void;
	handleMergeTags: (option: any, editorRef: React.RefObject<DynamicInlineEditorRef>) => void;
	editorKey: number;
	isListingOverlayMode?: boolean;
	scrollSuppressedTooltipProps?: Record<string, any>;
}

interface SocialMediaFooterElementProps {
	closeDrawer: () => void;
	onSave: () => void;
	isSaveDisabled: boolean;
	isSavingCaption: boolean;
	t: (key: string) => string;
}

interface SocialMediaCaptionDrawerProps {
	show: boolean;
	top?: string | number;
	templateState: TemplateState;
	fieldOptions: (key: string, fields: any) => any[];
	onSave?: (data: {
		showLinkPost: boolean;
		urlToSave: string;
		captionPlainText: string;
		linkTitlePlainText: string;
		linkDescriptionPlainText: string;
		currentEtc?: TemplateState['etc'];
	}) => Promise<boolean>;
	isSavingCaption?: boolean;
	t: (key: string) => string;
	onHide: () => void;
	container?: HTMLElement;
	isListingOverlayMode?: boolean;
}

// Returns a fresh value on every call: slate-react tracks node parents by object
// identity, so two editors mounted with the same node objects overwrite each
// other's mapping and ReactEditor.findPath throws "Unable to find the path".
const createEmptySlateValue = (): Descendant[] => [{ type: 'paragraph', children: [{ text: '' }] }];

const slateValueToPlainText = (value: Descendant[] = []): string => {
	let result = '';

	const hasNextParagraphSibling = (nodes: SlateNode[], startIndex: number): boolean => {
		for (let i = startIndex; i < nodes.length; i += 1) {
			const next = nodes[i];
			if (!next) continue;
			if (next.type === 'paragraph') return true;
		}
		return false;
	};

	const traverse = (nodes: SlateNode[]) => {
		for (let index = 0; index < nodes.length; index += 1) {
			const node = nodes[index];
			if (!node) continue;

			if (typeof node.text === 'string') {
				result += node.text;
				continue;
			}

			if (node.type === 'badge') {
				result += node.value ?? '';
				continue;
			}

			if (Array.isArray(node.children)) {
				traverse(node.children);

				if (node.type === 'paragraph' && hasNextParagraphSibling(nodes, index + 1)) {
					if (!result.endsWith('\n')) result += '\n';
				}
			}
		}
	};

	traverse(Array.isArray(value) ? value : []);
	return result;
};

const normalizeEtcToSlate = (incoming: string | SlateNode[] | any): Descendant[] => {
	if (Array.isArray(incoming)) return incoming as Descendant[];
	if (typeof incoming !== 'string') return createEmptySlateValue();

	const tokenRegex = /(\{\{[^}]+\}\})/g;
	const makeId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;
	const lines = incoming.split(/\r?\n/);

	return lines.map((line) => {
		const parts = String(line).split(tokenRegex);
		const children: Descendant[] = [];

		for (const part of parts) {
			if (!part) continue;
			if (tokenRegex.test(part)) {
				tokenRegex.lastIndex = 0;
				const inner = part.slice(2, -2).trim();
				const label = (dataFields as any)?.[inner]?.label;
				children.push({
					type: 'badge',
					label: label || inner || part,
					value: part,
					id: makeId(),
					children: [{ text: '' }],
				} as any);
			} else {
				children.push({ text: part });
			}
		}

		if (children.length === 0) {
			children.push({ text: '' });
		}
		if ('type' in children[0] && children[0]?.type === 'badge') {
			children.unshift({ text: '' });
		}
		if ('type' in children[children.length - 1] && children[children.length - 1]?.type === 'badge') {
			children.push({ text: '' });
		}

		return { type: 'paragraph', children };
	});
};

const shouldEnableLinkPostFromEtc = (etc?: TemplateState['etc']): boolean => {
	const hasText = (val: string | SlateNode[] | any) => slateValueToPlainText(normalizeEtcToSlate(val)).trim().length > 0;
	const url = String(etc?.linkUrl ?? etc?.url ?? '').trim();
	return hasText(etc?.linkTitle) || hasText(etc?.linkDescription) || url.length > 0;
};

const LinkPostForm: React.FC<LinkPostFormProps> = ({
	t,
	fieldOptions,
	linkTitleRef,
	linkDescRef,
	editorValues,
	setEditorValues,
	handleEmojiSelect,
	handleMergeTags,
	editorKey,
	isListingOverlayMode = false,
	scrollSuppressedTooltipProps = {},
}) => {
	return (
		<Form.Group className="mb-3 mt-3" id="linkPostForm-group">
			<div className="d-flex align-items-center justify-content-between">
				<div className="col">
					<Form.Label className="caption-editor-font">
						<span className="me-2 fs-8 text-uppercase text-secondary">{t('linkTitle')}</span>
						<BayTooltip overlay={t('linkTitleMessage')} placement="top" controlVisibilityProps={scrollSuppressedTooltipProps}>
							<BsQuestionCircle />
						</BayTooltip>
					</Form.Label>
				</div>
				<div className="col d-flex text-end justify-content-end">
					<VariableDropdown
						baseKey="linkPostForm-template"
						editorRef={linkTitleRef}
						fieldOptions={fieldOptions}
						t={t}
						onSelect={handleMergeTags}
					/>
				</div>
			</div>
			<div className="position-relative">
				<DynamicInlineEditor
					key={`${editorKey}-linkTitle`}
					ref={linkTitleRef}
					charLimit={40}
					height={45}
					placeholder="Enter"
					value={editorValues.linkTitle}
					onChange={(val) => setEditorValues((prev) => ({ ...prev, linkTitle: val }))}
				/>
				<span className="position-absolute mb-1 bottom-0  caption-editor-counter">
					<BayEmojiPicker placement="top" width={372} onEmojiSelect={(emoji) => handleEmojiSelect(emoji, linkTitleRef)}>
						<span className="cursor-pointer caption-editor-emoji d-inline-flex align-items-center justify-content-center">☺</span>
					</BayEmojiPicker>
					{linkTitleRef.current?.getTextLength() ?? 0}/40
				</span>
			</div>
			<div className="d-flex align-items-center justify-content-center gap-2 py-2 link-warning mt-3 rounded-2 caption-editor-font">
				<span className="text-center px-5 link-warning-text">{t('linkTitleWarning')}</span>
			</div>

			<div className="d-flex align-items-center justify-content-between mt-3">
				<div className="col">
					<Form.Label className="caption-editor-font">
						<span className="me-2 fs-8 text-uppercase text-secondary">{t('linkDescription')}</span>
						<BayTooltip overlay={t('linkDescriptionMessage')} placement="top" controlVisibilityProps={scrollSuppressedTooltipProps}>
							<BsQuestionCircle />
						</BayTooltip>
					</Form.Label>
				</div>
				<div className="col d-flex text-end justify-content-end">
					<VariableDropdown
						baseKey="linkPostDescription-template"
						editorRef={linkDescRef}
						fieldOptions={fieldOptions}
						t={t}
						onSelect={handleMergeTags}
					/>
				</div>
			</div>
			<div className="position-relative">
				<DynamicInlineEditor
					key={`${editorKey}-linkDescription`}
					ref={linkDescRef}
					charLimit={80}
					height={65}
					placeholder="Enter"
					value={editorValues.linkDescription}
					onChange={(val) => setEditorValues((prev) => ({ ...prev, linkDescription: val }))}
				/>
				<span className="position-absolute mb-1 bottom-0 caption-editor-counter">
					<BayEmojiPicker placement="top" width={372} onEmojiSelect={(emoji) => handleEmojiSelect(emoji, linkDescRef)}>
						<span className="cursor-pointer caption-editor-emoji d-inline-flex 	 align-items-center justify-content-center">☺</span>
					</BayEmojiPicker>
					{linkDescRef.current?.getTextLength() ?? 0}/80
				</span>
			</div>
			<div className="row mt-3">
				<div className="col">
					<Form.Label className="caption-editor-font">
						<span className="me-2 fs-8 text-secondary text-uppercase">{t('url')}</span>
					</Form.Label>
				</div>
			</div>
			{isListingOverlayMode ? (
				// Listing overlays always link to the single property website; URL is read-only.
				<BayTooltip
					overlay={t('listingOverlayUrlLockedTooltip')}
					placement="top"
					controlVisibilityProps={scrollSuppressedTooltipProps}
				>
					<div className="link-url-locked editor-inline border-0 caption-editor-font p-2 rounded-1 d-flex align-items-center">
						<span className="d-inline-flex align-items-center px-2 py-1 bg-primary-subtle rounded-1 text-dark text-nowrap user-select-none">
							{t('listingOverlaySinglePropertyWebsiteUrl')}
						</span>
					</div>
				</BayTooltip>
			) : (
				<Form.Control
					className="link-url editor-inline caption-editor-font"
					id="linkPostURL-template"
					placeholder="Enter"
					value={editorValues.url}
					onChange={(e) => setEditorValues((prev) => ({ ...prev, url: e.target.value }))}
				/>
			)}
		</Form.Group>
	);
};

const SocialMediaFooterElement: React.FC<SocialMediaFooterElementProps> = ({ closeDrawer, onSave, isSaveDisabled, isSavingCaption, t }) => {
	return (
		<div className="border-top p-3 px-4">
			<div className="d-flex align-items-center">
				<Form.Group className="form-item mb-3 d-flex justify-content-between w-100">
					<Button
						className="me-2"
						disabled={isSavingCaption}
						variant="secondary"
						onClick={() => closeDrawer()}
					>
						{t('cancel')}
					</Button>
					<Button
						disabled={isSaveDisabled || isSavingCaption}
						variant="primary"
						onClick={onSave}
					>
						{isSavingCaption ? (
							<>
								<Spinner animation="border" className="me-2" size="sm" />
								{t('save')}
							</>
						) : (
							t('save')
						)}
					</Button>
				</Form.Group>
			</div>
		</div>
	);
};

const SocialMediaCaptionDrawer: React.FC<SocialMediaCaptionDrawerProps> = ({
	show,
	top,
	templateState,
	fieldOptions,
	onSave = async () => false,
	isSavingCaption = false,
	t,
	onHide,
	container,
	// In listing-overlay mode the link-post toggle is locked on and the URL is not user-adjustable.
	isListingOverlayMode = false,
}) => {
	// Listing overlays link to the single-property-website server-side, so the URL carries no value
	// in the editor — only the read-only badge is shown.
	const initialUrl = isListingOverlayMode
		? ''
		: (templateState?.etc?.linkUrl || '');

	const [showLinkPost, setShowLinkPost] = useState(isListingOverlayMode);
	const [editorValues, setEditorValues] = useState<EditorValues>({
		caption: normalizeEtcToSlate(templateState?.etc?.caption),
		linkTitle: normalizeEtcToSlate(templateState?.etc?.linkTitle),
		linkDescription: normalizeEtcToSlate(templateState?.etc?.linkDescription),
		url: initialUrl,
	});
	const [editorKey, setEditorKey] = useState(0);

	const captionEditorRef = useRef<DynamicInlineEditorRef>(null);
	const linkTitleRef = useRef<DynamicInlineEditorRef>(null);
	const linkDescRef = useRef<DynamicInlineEditorRef>(null);
	const wasShownRef = useRef(false);

	// rc-tooltip positions itself once on open and doesn't track this Offcanvas.Body's
	// own scroll (only window scroll/resize), so it drifts away from its trigger while
	// scrolling. Force every tooltip closed for the duration of the scroll.
	const [suppressTooltips, setSuppressTooltips] = useState(false);
	const scrollHideTimeoutRef = useRef(null);

	const handleBodyScroll = useCallback(() => {
		setSuppressTooltips(true);
		clearTimeout(scrollHideTimeoutRef.current);
		scrollHideTimeoutRef.current = setTimeout(() => setSuppressTooltips(false), 150);
	}, []);

	useEffect(() => () => clearTimeout(scrollHideTimeoutRef.current), []);

	const scrollSuppressedTooltipProps = suppressTooltips ? { visible: false } : {};

	const resetEditorState = useCallback(() => {
		setShowLinkPost(isListingOverlayMode ? true : shouldEnableLinkPostFromEtc(templateState?.etc));
		setEditorValues({
			caption: normalizeEtcToSlate(templateState?.etc?.caption),
			linkTitle: normalizeEtcToSlate(templateState?.etc?.linkTitle),
			linkDescription: normalizeEtcToSlate(templateState?.etc?.linkDescription),
			url: isListingOverlayMode
				? ''
				: (templateState?.etc?.linkUrl || ''),
		});
		setEditorKey((k) => k + 1);
	}, [templateState, isListingOverlayMode]);

	useEffect(() => {
		if (show && !wasShownRef.current) {
			resetEditorState();
		}
		wasShownRef.current = show;
	}, [show, templateState]);

	const captionPlainText = slateValueToPlainText(editorValues.caption);
	const linkTitlePlainText = slateValueToPlainText(editorValues.linkTitle);
	const linkDescriptionPlainText = slateValueToPlainText(editorValues.linkDescription);
	const isSaveDisabled =
		captionPlainText.trim().length === 0 ||
		(showLinkPost && (
			linkTitlePlainText.trim().length === 0 ||
			linkDescriptionPlainText.trim().length === 0 ||
			(!isListingOverlayMode && (editorValues.url ?? '').trim().length === 0)
		));

	const handleEmojiSelect = (emoji: string, editorRef: React.RefObject<DynamicInlineEditorRef>) => {
		editorRef.current?.insertEmoji(emoji);
	};

	const handleMergeTags = (option: any, editorRef: React.RefObject<DynamicInlineEditorRef>) => {
		editorRef.current?.insertVariable(option.label, option.value);
	};

	const handleShowLinkPostToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
		// Listing overlays are always link posts; the toggle is locked on.
		if (isListingOverlayMode) {
			return;
		}
		// Keep the link fields so an accidental off/on doesn't lose them; onSave
		// already blanks them when showLinkPost is false.
		setShowLinkPost(e.target.checked);
	};

	const handleClose = () => {
		resetEditorState();
		onHide();
	};

	return (
		<Offcanvas
			className="text-dark caption-editor-drawer "
			container={container || document.getElementsByClassName('polotno-workspace-container')[0]}
			id="caption_editor_drawer"
			placement="end"
			show={show}
			style={{ top }}
			onHide={handleClose}
		>
			<Offcanvas.Header closeButton className="border-bottom p-3 px-3 ">
				<div className="offcanvas-title h3">{t('socialMediaCaptions')}</div>
			</Offcanvas.Header>

			<Offcanvas.Body className="overflow-x-hidden" onScroll={handleBodyScroll}>
				<Form.Group className="mb-3 captionForm-group" id="captionForm-template-group">
					<div className="row">
						<div className="col">
							<Form.Label className="caption-editor-font">
								<span className="me-2">{t('mainPost')}</span>
								<BayTooltip overlay={t('mainPostMessage')} placement="bottom" controlVisibilityProps={scrollSuppressedTooltipProps}>
									<BsQuestionCircle />
								</BayTooltip>
							</Form.Label>
						</div>
						<div className="col d-flex text-end justify-content-end">
							<VariableDropdown
								baseKey="captionForm-template"
								editorRef={captionEditorRef}
								fieldOptions={fieldOptions}
								t={t}
								onSelect={handleMergeTags}
							/>
						</div>
					</div>

					<div className="position-relative">
						<DynamicInlineEditor
							key={`${editorKey}-caption`}
							ref={captionEditorRef}
							charLimit={2500}
							height={160}
							placeholder={t('likeToshare')}
							value={editorValues.caption}
							onChange={(val) => setEditorValues((prev) => ({ ...prev, caption: val }))}
						/>
						<span className="position-absolute mb-1 bottom-0 caption-editor-counter">
							<BayEmojiPicker width={372} onEmojiSelect={(emoji) => handleEmojiSelect(emoji, captionEditorRef)}>
								<span className="cursor-pointer caption-editor-emoji d-inline-flex align-items-center justify-content-center">☺</span>
							</BayEmojiPicker>
							{captionEditorRef.current?.getTextLength() ?? 0}/2,500
						</span>
					</div>

					<Form.Label className="mt-3 pt-3 pb-1 me-2 caption-editor-font" htmlFor="margin_switch">
						{t('linkPostTemplate')}
					</Form.Label>
					<BayTooltip className="mb-1"
						id="linkPostTooltip"
						overlay={t('linkDescriptionMessage')}
						placement="bottom"
						controlVisibilityProps={scrollSuppressedTooltipProps}
					>
						<BsQuestionCircle />
					</BayTooltip>
					<BayTooltip
						overlay={isListingOverlayMode ? t('listingOverlayLinkPostLockedTooltip') : ''}
						placement="topLeft"
						disabled={!isListingOverlayMode}
						controlVisibilityProps={scrollSuppressedTooltipProps}
					>
						<div className="d-table">
							<Form.Check
								checked={isListingOverlayMode ? true : showLinkPost}
								id="link_post_switch"
								label={`${(isListingOverlayMode ? true : showLinkPost) ? t('yes') : t('no')}`}
								type="switch"
								onChange={handleShowLinkPostToggle}
								disabled={isListingOverlayMode}
								className="custom-caption-switch"
							/>
						</div>
					</BayTooltip>
					{showLinkPost && (
						<LinkPostForm
							editorKey={editorKey}
							editorValues={editorValues}
							fieldOptions={fieldOptions}
							handleEmojiSelect={handleEmojiSelect}
							handleMergeTags={handleMergeTags}
							linkDescRef={linkDescRef}
							linkTitleRef={linkTitleRef}
							setEditorValues={setEditorValues}
							t={t}
							isListingOverlayMode={isListingOverlayMode}
							scrollSuppressedTooltipProps={scrollSuppressedTooltipProps}
						/>
					)}
				</Form.Group>
			</Offcanvas.Body>

			<SocialMediaFooterElement
				closeDrawer={handleClose}
				isSaveDisabled={isSaveDisabled}
				isSavingCaption={isSavingCaption}
				t={t}
				onSave={() => {
					if (isSaveDisabled || isSavingCaption) return;

					onSave({
						showLinkPost,
						urlToSave: String(editorValues.url ?? '').trim(),
						captionPlainText,
						linkTitlePlainText,
						linkDescriptionPlainText,
						currentEtc: templateState?.etc,
					}).then((isSaved) => {
						if (isSaved) {
							handleClose();
						}
					});
				}}
			/>
		</Offcanvas>
	);
};

export default SocialMediaCaptionDrawer;
